<osp-stepper [formGroup]="form" #stepper>
  <!-- STEP #1 - STOCK TYPE -->
  @if (!isEditMode) {
    <osp-step
      [title]="'modules.locations.stock.create.typeSelection.title' | translate"
      [icon]="IconKey.StockCreateType | icon"
      [control]="form.controls.stockType"
    >
      <ng-template ospStepContent>
        <div class="step-content">
          <div class="w-full h-full flex gap-4 stock-type-step-layout">
            <div class="w-full flex flex-column">
              <div
                class="step-title"
                [innerHTML]="'modules.locations.stock.create.typeSelection.subtitle' | translate"
              ></div>
              <osp-card-option-list class="stock-type-options" formControlName="stockType" [options]="stockTypes" />
            </div>
          </div>
        </div>
      </ng-template>
      <ng-template ospStepFooter let-nextCallback="nextCallback" let-isValid="isValid">
        <div class="flex align-items-center justify-content-between">
          <ng-container [ngTemplateOutlet]="btnCancel" />
          <ng-container
            [ngTemplateOutlet]="btnNext"
            [ngTemplateOutletContext]="{ onClick: nextCallback, disabled: !isValid }"
          />
        </div>
      </ng-template>
    </osp-step>
  }

  <!-- STEP #2 - STOCK DATA -->
  <osp-step
    [title]="'modules.locations.stock.create.stockData.title' | translate"
    [icon]="IconKey.EquipmentCreateMainData | icon"
    [control]="form.controls.stockData"
  >
    <ng-template ospStepContent>
      <div class="step-content" [formGroup]="form.controls.stockData">
        <div class="grid">
          <div class="col-9">
            <h3 class="font-bold text-color mb-2">
              {{ 'modules.locations.stock.create.form.requiredInfo' | translate }}
            </h3>
            <div class="grid mb-2">
              <osp-form-field class="col-6">
                <osp-label>{{ 'modules.locations.stock.create.form.stockName.label' | translate }}</osp-label>
                <osp-input-text
                  formControlName="stockName"
                  [placeholder]="'modules.locations.stock.create.form.stockName.placeholder' | translate"
                />
                <osp-error [messages]="formService.stockNameErrorMessages" />
              </osp-form-field>

              <osp-form-field class="col-6">
                <osp-label>{{ 'general.org.s' | translate }}</osp-label>
                <osp-dropdown
                  formControlName="organisationId"
                  [options]="$organisations()"
                  [placeholder]="'modules.locations.stock.create.form.organisation.placeholder' | translate"
                  [showClear]="true"
                  [filter]="true"
                />
                <osp-error [messages]="formService.organisationErrorMessages" />
              </osp-form-field>
            </div>

            <h3 class="font-bold text-color mb-2">
              {{ 'modules.locations.stock.create.form.additionalInfo' | translate }}
            </h3>
            <div class="grid">
              <osp-form-field class="col-6">
                <osp-label>{{ 'modules.locations.stock.create.form.costCenter.label' | translate }}</osp-label>
                <osp-input-text
                  formControlName="stockCostCenter"
                  [placeholder]="'modules.locations.stock.create.form.costCenter.placeholder' | translate"
                />
                <osp-error [messages]="formService.stockCostCenterErrorMessages" />
              </osp-form-field>
            </div>
          </div>
          <div class="step-content-form-card col-3">
            <ng-container
              [ngTemplateOutlet]="infoBox"
              [ngTemplateOutletContext]="{
                label: $selectedStockType(),
                icon: $selectedStockTypeIcon(),
                description: $selectedStockTypeInfoDescription() | translate
              }"
            />
          </div>
        </div>
      </div>
    </ng-template>
    <ng-template ospStepFooter let-prevCallback="prevCallback" let-nextCallback="nextCallback" let-isValid="isValid">
      <div class="flex align-items-center justify-content-between">
        <ng-container [ngTemplateOutlet]="btnCancel" />
        @if (isEditMode) {
          <ng-container [ngTemplateOutlet]="btnSave" [ngTemplateOutletContext]="{ disabled: !isValid }" />
        } @else {
          <div class="flex gap-2">
            <ng-container [ngTemplateOutlet]="btnBack" [ngTemplateOutletContext]="{ onClick: prevCallback }" />
            @if ($showAddressStep()) {
              <ng-container
                [ngTemplateOutlet]="btnNext"
                [ngTemplateOutletContext]="{ onClick: nextCallback, disabled: !isValid }"
              />
            } @else {
              <ng-container [ngTemplateOutlet]="btnSave" [ngTemplateOutletContext]="{ disabled: !isValid }" />
            }
          </div>
        }
      </div>
    </ng-template>
  </osp-step>

  <!-- STEP #3 - ADDRESS -->
  @if (!isEditMode && $showAddressStep()) {
    <osp-step
      [title]="'modules.locations.stock.create.form.address.title' | translate"
      [icon]="IconKey.AutomatedTransferMapAddress | icon"
      [control]="form.controls.stockAddress"
    >
      <ng-template ospStepContent>
        <div class="step-content" [formGroup]="form.controls.stockAddress">
          <div class="grid">
            <div class="col-9">
              <osp-location-address-form
                [formGroup]="form.controls.stockAddress"
                [mapCenter]="mapCenter"
                [mapMarkerPosition]="mapMarkerPosition"
                [isMapLoaded]="(isMapLoaded$ | async) ?? false"
                [showAutomaticGeofence]="$showAutomaticGeofence()"
                (mapClick)="handleMapClick($event)"
                (locationChanges)="handleSearchLocationChange($event)"
              />
            </div>
            <div class="step-content-form-card col-3">
              <ng-container
                [ngTemplateOutlet]="infoBox"
                [ngTemplateOutletContext]="{
                  label: $selectedStockType(),
                  icon: $selectedStockTypeIcon(),
                  description: $selectedStockTypeInfoDescription() | translate
                }"
              />
            </div>
          </div>
        </div>
      </ng-template>
      <ng-template ospStepFooter let-prevCallback="prevCallback" let-nextCallback="nextCallback" let-isValid="isValid">
        <div class="flex align-items-center justify-content-between">
          <ng-container [ngTemplateOutlet]="btnCancel" />
          <div class="flex gap-2">
            <ng-container [ngTemplateOutlet]="btnBack" [ngTemplateOutletContext]="{ onClick: prevCallback }" />
            <ng-container [ngTemplateOutlet]="btnSave" [ngTemplateOutletContext]="{ disabled: !isValid }" />
          </div>
        </div>
      </ng-template>
    </osp-step>
  }
</osp-stepper>

<!-- Templates -->
<ng-template #infoBox let-label="label" let-icon="icon" let-description="description">
  <osp-card-option [label]="label" [icon]="icon" [description]="description" />
</ng-template>

<ng-template #btnCancel>
  <osp-button
    [label]="'general.buttons.cancel' | translate"
    [icon]="IconKey.GlobalCancel | icon"
    [category]="'ghost'"
    (onClick)="onCancel()"
  />
</ng-template>

<ng-template #btnBack let-onClick="onClick">
  <osp-button
    [label]="'general.buttons.back' | translate"
    [icon]="IconKey.GlobalFormBack | icon"
    [category]="'secondary'"
    (onClick)="onClick()"
  />
</ng-template>

<ng-template #btnNext let-onClick="onClick" let-disabled="disabled">
  <osp-button
    [label]="'general.buttons.next' | translate"
    [icon]="IconKey.GlobalFormNext | icon"
    (onClick)="onClick()"
    [disabled]="disabled"
  />
</ng-template>

<ng-template #btnSave let-disabled="disabled">
  <osp-button
    [label]="'general.buttons.save' | translate"
    [icon]="IconKey.GlobalSave | icon"
    (onClick)="handleSubmit()"
    [disabled]="disabled"
  />
</ng-template>
